import type { DashboardAnalyticAction } from 'models';
import { FC, ReactNode, useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import {
  dashboardByClientFilterChanged,
  dashboardByPinnedFilterChanged,
  dashboardByStatusFilterChanged,
  dashboardFilterTypeChanged,
  dashboardPinnedGroupColorChanged,
  dashboardPinnedGroupRenamed,
  dashboardQuickSearchTermChanged,
} from 'state/quotes/dashboard/dashboard.actions';
import { tileRemovedThunk } from 'state/quotes/dashboard/dashboard.thunks';
import type { FilterType, StatusFilter } from 'state/quotes/dashboard/filter/filter.model';
import type { GroupColor } from 'state/quotes/dashboard/tiles/tiles.model';
import { dashboard } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { ActiveQuoteDashboard } from 'state/workspaces/workspaces/workspaces.model';

import { Dashboard } from './Dashboard';
import { DashboardSkeleton } from './skeleton/Dashboard.skeleton';

function getAnalyticActionFromFilterType(type: FilterType): DashboardAnalyticAction {
  switch (type) {
    case 'byClient':
      return 'Dashboard switch to filtering by client/product';
    case 'byPinned':
      return 'Dashboard switch to filtering by group/pin';
    case 'byStatus':
      return 'Dashboard switch to filtering by status';
    default:
      return '' as never;
  }
}

const ConnectedDashboard: FC<{ onDashboardClosed(): void; className?: string }> = ({
  onDashboardClosed,
  className,
}) => {
  const dispatch = useDispatch<ThunkDispatch>();

  const quotes = useSelector(dashboard.getAllQuotesIndexes, shallowEqual);
  const selectedFilter = useSelector(dashboard.getSelectedFilter);
  const quickSearchTerm = useSelector(dashboard.getQuickSearchTerm);
  const customGroups = useSelector(dashboard.getCustomGroups);
  const isLoading = useSelector(dashboard.isDashboardReloadedTileLoading);

  const onFilterTypeChanged = useCallback(
    (filterType: FilterType) => {
      logAnalytics.trackEvent('Active quotes dashboard', getAnalyticActionFromFilterType(filterType));
      dispatch(dashboardFilterTypeChanged(filterType));
    },
    [dispatch],
  );

  const onByStatusFilterChanged = useCallback(
    (filter: StatusFilter) => dispatch(dashboardByStatusFilterChanged(filter)),
    [dispatch],
  );

  const onByClientFilterChanged = useCallback(
    (client: string, product?: string) => dispatch(dashboardByClientFilterChanged(client, product)),
    [dispatch],
  );

  const onByPinnedFilterChanged = useCallback(
    (pinnedGroup?: string) => dispatch(dashboardByPinnedFilterChanged(pinnedGroup)),
    [dispatch],
  );

  const onCustomGroupRenamed = useCallback(
    (previousGroupName: string, newGroupName: string) => {
      dispatch(dashboardPinnedGroupRenamed(previousGroupName, newGroupName));
      logAnalytics.trackEvent('Active quotes dashboard', 'Rename a custom group');
    },
    [dispatch],
  );

  const onPinnedGroupColorChanged = useCallback(
    (pinnedGroup: string, color?: GroupColor) => {
      dispatch(dashboardPinnedGroupColorChanged(pinnedGroup, color));
    },
    [dispatch],
  );

  const onQuickSearchTermChanged = useCallback(
    (term?: string) => {
      dispatch(dashboardQuickSearchTermChanged(term));
    },
    [dispatch],
  );

  const onTilesDeleted = useCallback(
    (quoteIdsToDelete: string[]) => {
      logAnalytics.trackEvent('Active quotes dashboard', 'Closing many dashboard tiles');
      dispatch(tileRemovedThunk(...quoteIdsToDelete));
    },
    [dispatch],
  );
  const onTileClosed = useCallback(
    (quoteId: string) => {
      logAnalytics.trackEvent('Active quotes dashboard', 'Close a dashboard tile');
      dispatch(tileRemovedThunk(quoteId));
    },
    [dispatch],
  );

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <Dashboard
      className={className}
      quotes={quotes}
      selectedFilter={selectedFilter}
      quickSearchTerm={quickSearchTerm}
      onDashboardClosed={onDashboardClosed}
      onTileClosed={onTileClosed}
      onFilterTypeChanged={onFilterTypeChanged}
      onByStatusFilterChanged={onByStatusFilterChanged}
      onByClientFilterChanged={onByClientFilterChanged}
      onByPinnedFilterChanged={onByPinnedFilterChanged}
      onCustomGroupRenamed={onCustomGroupRenamed}
      onPinnedGroupColorChanged={onPinnedGroupColorChanged}
      onQuickSearchTermChanged={onQuickSearchTermChanged}
      onTilesDeleted={onTilesDeleted}
      customGroups={customGroups}
    />
  );
};

ConnectedDashboard.displayName = 'ConnectedDashboard';

type WithConnectedDashboardProps = Readonly<{
  children: ReactNode;
  isActive: ActiveQuoteDashboard;
  onHide?: () => void;
}>;

export const WithConnectedDashboard: FC<WithConnectedDashboardProps> = ({ isActive, onHide, children }) => {
  if (isActive === 'show' && onHide !== undefined) {
    return <ConnectedDashboard onDashboardClosed={onHide} className="h-100" />;
  }
  return <>{children}</>;
};

WithConnectedDashboard.displayName = 'WithConnectedDashboard';
